import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, RefreshCw, TrendingUp, Users, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiCall, formatCents } from '@/lib/api';

interface RevenueRow { month: string; payments_cents: number; day_passes_cents: number; store_cents: number; total_cents: number }
interface RevenueResp { from: string; to: string; rows: RevenueRow[]; totals: { payments_cents: number; day_passes_cents: number; store_cents: number; total_cents: number } }
interface AttendanceRow { day: string; total: number; members: number; day_passes: number }
interface AttendanceResp { from: string; to: string; rows: AttendanceRow[] }
interface TopMemberRow { id: string; name: string; checkins: number }
interface ClassRow { id: string; class_name: string; sessions: number; total_registrations: number }
interface ClassAttRow { id: string; class_name: string; color: string; checkins: number; unique_members: number }
interface ClassAttResp { from: string; to: string; rows: ClassAttRow[]; unclassified: number }
interface GrowthResp {
  from: string; to: string;
  signups: Array<{ day: string; n: number }>;
  cancellations: Array<{ day: string; n: number }>;
  mrr_by_month: Array<{ month: string; subs_started: number; cents_started: number }>;
}

export default function Reports() {
  const { getToken } = useAuth();
  const [rev, setRev] = useState<RevenueResp | null>(null);
  const [att, setAtt] = useState<AttendanceResp | null>(null);
  const [topMembers, setTopMembers] = useState<TopMemberRow[]>([]);
  const [classRows, setClassRows] = useState<ClassRow[]>([]);
  const [classAtt, setClassAtt] = useState<ClassAttResp | null>(null);
  const [growth, setGrowth] = useState<GrowthResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const [r, a, t, c, ca, g] = await Promise.all([
        apiCall<RevenueResp>('/v1/reports/revenue', { bearerToken: token }),
        apiCall<AttendanceResp>('/v1/reports/attendance', { bearerToken: token }),
        apiCall<{ rows: TopMemberRow[] }>('/v1/reports/top-members', { bearerToken: token }),
        apiCall<{ rows: ClassRow[] }>('/v1/reports/classes', { bearerToken: token }),
        apiCall<ClassAttResp>('/v1/reports/classes-attendance', { bearerToken: token }),
        apiCall<GrowthResp>('/v1/reports/growth', { bearerToken: token }),
      ]);
      setRev(r); setAtt(a); setTopMembers(t.rows); setClassRows(c.rows); setClassAtt(ca); setGrowth(g);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  const maxRevenue = Math.max(1, ...(rev?.rows ?? []).map((r) => r.total_cents));
  const maxAttendance = Math.max(1, ...(att?.rows ?? []).map((r) => r.total));

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Reports</h1>
          <p className="text-sm text-muted-foreground">Last 90 days.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
        </Button>
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {/* Revenue summary cards */}
      {rev && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Total revenue</CardDescription>
              <CardTitle className="text-3xl">{formatCents(rev.totals.total_cents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">Dues + day passes + store</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Member payments</CardDescription>
              <CardTitle className="text-3xl">{formatCents(rev.totals.payments_cents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">Monthly dues + manual entries</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Day passes</CardDescription>
              <CardTitle className="text-3xl">{formatCents(rev.totals.day_passes_cents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">Visitor walk-ins</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Store sales</CardDescription>
              <CardTitle className="text-3xl">{formatCents(rev.totals.store_cents)}</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">Front-desk POS</CardContent>
          </Card>
        </div>
      )}

      {/* Revenue by month bar chart (HTML) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Revenue by month</CardTitle>
          <CardDescription>Members + day passes, stacked.</CardDescription>
        </CardHeader>
        <CardContent>
          {!rev || rev.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No revenue data in this range.</p>
          ) : (
            <ul className="space-y-2">
              {rev.rows.map((r) => (
                <li key={r.month} className="grid grid-cols-12 items-center gap-3 text-sm">
                  <span className="col-span-2 tabular-nums text-muted-foreground">{r.month}</span>
                  <div className="col-span-8 h-5 bg-muted rounded overflow-hidden flex">
                    <div className="bg-indigo-500" style={{ width: `${(r.payments_cents / maxRevenue) * 100}%` }} title={`payments: ${formatCents(r.payments_cents)}`} />
                    <div className="bg-emerald-500" style={{ width: `${(r.day_passes_cents / maxRevenue) * 100}%` }} title={`day passes: ${formatCents(r.day_passes_cents)}`} />
                    <div className="bg-amber-500" style={{ width: `${(r.store_cents / maxRevenue) * 100}%` }} title={`store: ${formatCents(r.store_cents)}`} />
                  </div>
                  <span className="col-span-2 tabular-nums text-right font-medium">{formatCents(r.total_cents)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Attendance by day (recent 30) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-4 w-4" /> Check-ins by day</CardTitle>
          <CardDescription>Recent 30 days.</CardDescription>
        </CardHeader>
        <CardContent>
          {!att || att.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No check-ins in this range.</p>
          ) : (
            <ul className="space-y-1.5">
              {att.rows.slice(-30).map((r) => (
                <li key={r.day} className="grid grid-cols-12 items-center gap-3 text-xs">
                  <span className="col-span-3 tabular-nums text-muted-foreground">{r.day}</span>
                  <div className="col-span-7 h-3 bg-muted rounded overflow-hidden">
                    <div className="bg-indigo-500 h-full" style={{ width: `${(r.total / maxAttendance) * 100}%` }} />
                  </div>
                  <span className="col-span-2 tabular-nums text-right font-medium">{r.total}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Top members */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Users className="h-4 w-4" /> Top members</CardTitle>
            <CardDescription>Most check-ins in the last 90 days.</CardDescription>
          </CardHeader>
          <CardContent>
            {topMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No check-in data.</p>
            ) : (
              <ul className="space-y-1.5">
                {topMembers.map((m, i) => (
                  <li key={m.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">
                      <span className="text-muted-foreground tabular-nums mr-2 text-xs">#{i + 1}</span>
                      {m.name}
                    </span>
                    <span className="font-medium tabular-nums">{m.checkins}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Class popularity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-4 w-4" /> Class popularity</CardTitle>
            <CardDescription>Registrations per class in the last 90 days.</CardDescription>
          </CardHeader>
          <CardContent>
            {classRows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No classes scheduled yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {classRows.map((c) => (
                  <li key={c.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{c.class_name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {c.total_registrations} reg / {c.sessions} session{c.sessions === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attendance by class (historical check-ins linked to a class) */}
      {classAtt && classAtt.rows.length > 0 && (() => {
        const maxCheckins = Math.max(1, ...classAtt.rows.map((r) => r.checkins));
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Calendar className="h-4 w-4" /> Attendance by class</CardTitle>
              <CardDescription>
                Actual check-ins linked to a class, last 90 days. {classAtt.unclassified > 0 && (
                  <span className="text-amber-700 dark:text-amber-400">{classAtt.unclassified} unclassified.</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {classAtt.rows.map((c) => (
                  <li key={c.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate font-medium">{c.class_name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {c.checkins.toLocaleString()} check-ins · {c.unique_members} unique
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${(c.checkins / maxCheckins) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })()}

      {/* Growth (signups per day + MRR per month) */}
      {growth && (growth.signups.length > 0 || growth.mrr_by_month.length > 0) && (() => {
        const totalSignups = growth.signups.reduce((s, r) => s + r.n, 0);
        const totalCancels = growth.cancellations.reduce((s, r) => s + r.n, 0);
        const maxSignups = Math.max(1, ...growth.signups.map((r) => r.n));
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Growth</CardTitle>
              <CardDescription>
                {totalSignups} signup{totalSignups === 1 ? '' : 's'} · {totalCancels} cancellation{totalCancels === 1 ? '' : 's'} in this window.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {growth.signups.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground mb-1">Signups per day</div>
                  <div className="flex items-end gap-px h-16">
                    {growth.signups.map((r) => (
                      <div key={r.day} className="flex-1 bg-primary rounded-sm" style={{ height: `${(r.n / maxSignups) * 100}%` }} title={`${r.day}: ${r.n}`} />
                    ))}
                  </div>
                </div>
              )}
              {growth.mrr_by_month.length > 0 && (
                <ul className="mt-4 divide-y">
                  {growth.mrr_by_month.map((m) => (
                    <li key={m.month} className="py-1.5 flex items-center justify-between text-sm">
                      <span>{m.month}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        +{m.subs_started} sub{m.subs_started === 1 ? '' : 's'} · {formatCents(m.cents_started)}/mo
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
